import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  OrderPayoutResponse,
  OrderPayoutRequest,
  getPayoutOrdersApi,
  OrderPayout,
  applyOrderForPayoutApi,
  uploadReceiptApi,
  PayOutUploadResponse,
  payOutOrderReviewApi,
  PayOutReviewRequest,
  cancelPayOutOrderApi,
  PayOutCancelRequest,
} from "./api";
import { State } from "@features/base/types";
// import { store } from "@app/store";
// import { addAlert } from "@features/alert/slice";

export const getPayoutOrders = createAsyncThunk<
  OrderPayoutResponse,
  { userId: string; data: OrderPayoutRequest }
>(
  "payouts/getPayoutOrdersApi",
  async ({ userId, data }, { rejectWithValue }) => {
    try {
      return await getPayoutOrdersApi(userId, data);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const applyOrderForPayout = createAsyncThunk<
  string,
  { orderId: string }
>("payouts/applyOrderForPayout", async ({ orderId }, { rejectWithValue }) => {
  try {
    return await applyOrderForPayoutApi(orderId);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const uploadReceipt = createAsyncThunk<
  PayOutUploadResponse,
  { orderId: string; file: FileList }
>("payouts/uploadReceipt", async ({ orderId, file }, { rejectWithValue }) => {
  try {
    return await uploadReceiptApi(orderId, file);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const payOutOrderReview = createAsyncThunk<
  string,
  { data: PayOutReviewRequest }
>("payouts/payOutOrderReview", async ({ data }, { rejectWithValue }) => {
  try {
    return await payOutOrderReviewApi(data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const cancelPayOutOrder = createAsyncThunk<
  string,
  { orderId: string, data: PayOutCancelRequest }
>("payouts/cancelPayOutOrder", async ({ orderId, data }, { rejectWithValue }) => {
  try {
    return await cancelPayOutOrderApi(orderId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

type PayoutOrdersState = State<{
  payoutOrders: OrderPayout[] | null;
  page: number;
  totalCount: number;
  payoutOrdersLoading: boolean;
  isReceiptLoading: boolean;
  receiptUrls: string[];
  message: string;
  isReviewLoading: boolean;
}>;

const initialState: PayoutOrdersState = {
  payoutOrders: null,
  loading: false,
  payoutOrdersLoading: false,
  error: null,
  page: 1,
  totalCount: 0,
  isReceiptLoading: false,
  receiptUrls: [],
  message: "",
  isReviewLoading: false,
};

const payoutOrdersSlice = createSlice({
  name: "payouts",
  initialState,
  reducers: {
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload;
    },
    resetOrder: (state) => {
      state.receiptUrls = [];
      state.message = "";
    },
    setReceipts: (state, action: PayloadAction<string[]>) => {
      state.receiptUrls = action.payload;
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.receiptUrls = state.receiptUrls.filter(
        (url) => url !== action.payload
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPayoutOrders.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPayoutOrders.fulfilled, (state, action) => {
        state.loading = false;
        state.payoutOrders = action.payload?.user_pay_out_order;
        state.page = action.payload?.page;
        state.totalCount = action.payload?.count;
      })
      .addCase(getPayoutOrders.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(applyOrderForPayout.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyOrderForPayout.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
        // store.dispatch(addAlert(action.payload));
      })
      .addCase(applyOrderForPayout.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(uploadReceipt.pending, (state) => {
        state.isReceiptLoading = true;
      })
      .addCase(uploadReceipt.fulfilled, (state, action) => {
        state.isReceiptLoading = false;
        if (action.payload.urls != null)
          state.receiptUrls = [...state.receiptUrls, ...action.payload.urls];
      })
      .addCase(uploadReceipt.rejected, (state) => {
        state.isReceiptLoading = false;
      })
      .addCase(payOutOrderReview.pending, (state) => {
        state.isReviewLoading = true;
        state.error = null;
      })
      .addCase(payOutOrderReview.fulfilled, (state, action) => {
        state.isReviewLoading = false;
        console.log(action.payload);
        // store.dispatch(addAlert(action.payload));
      })
      .addCase(payOutOrderReview.rejected, (state, action) => {
        state.isReviewLoading = false;
        state.error = action.payload as string;
      })
      .addCase(cancelPayOutOrder.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelPayOutOrder.fulfilled, (state, action) => {
        state.loading = false;
        console.log(action.payload);
        // store.dispatch(addAlert(action.payload));
      })
      .addCase(cancelPayOutOrder.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default payoutOrdersSlice.reducer;

export const { setReceipts, setMessage, resetOrder, removeFile } = payoutOrdersSlice.actions;
